// Keep a chat's conversation across a reload (TOM-252).
//
// The server owns the conversation (ADR 0100). The page holds only its id, and
// that id lived in React state, so a reload started a new conversation. Since
// Neomaris writes one summary per conversation once it has been quiet for half
// an hour, and reads the recent and relevant summaries into its answers (ADR
// 0250, TOM-252), a sitting split by reloads became several thin summaries. So
// each chat surface keeps its conversation id in sessionStorage: this tab only,
// gone when the tab closes.
//
// One record per surface, holding the account and the boat the conversation
// was started in. A read for another account or another boat finds nothing and
// clears the record. That read is the boat change, whether the page switched
// boats in place or remounted the chat in another shell, so a conversation is
// never resumed after the owner has moved to another boat, as before.
//
// The server still decides. An id it no longer knows answers 404
// `conversation_not_found` before the model is called; the chat clears the
// record and asks again in a new conversation (isNeoConversationGone).
//
// Every storage access is wrapped: with storage blocked or full the chat just
// starts fresh after a reload, as it did before. Pure enough to test under
// Node: the storage is passed in.

/** The chat surfaces that keep a conversation: the chat panel and the Alarms page's mate bar. */
export type NeoChatSurface = 'chat' | 'mate';

/** Who and where a conversation belongs to. `boatId` is null for the chat with no boat. */
export interface NeoConversationScope {
  userId: string;
  boatId: string | null;
}

interface StoredNeoConversation extends NeoConversationScope {
  conversationId: string;
}

type ResumeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const sessionStore = (): ResumeStorage => window.sessionStorage;

// The chat route takes only a UUID (z.uuid()). Anything else would fail every
// send with a 400 until the tab closed, so it is never sent.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The sessionStorage key for one surface's conversation. */
export function neoConversationKey(surface: NeoChatSurface): string {
  return `tombolo.neo.conversation.${surface}`;
}

/** Decode a stored record, or null when it is missing or malformed. */
export function parseStoredNeoConversation(raw: string | null): StoredNeoConversation | null {
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { userId, boatId, conversationId } = parsed as Record<string, unknown>;
  if (typeof userId !== 'string' || userId.length === 0) return null;
  if (boatId !== null && (typeof boatId !== 'string' || boatId.length === 0)) return null;
  if (typeof conversationId !== 'string' || !UUID.test(conversationId)) return null;
  return { userId, boatId, conversationId };
}

/**
 * The conversation to continue on this surface, or null to start a new one.
 * A record for another account or boat, or a malformed one, is cleared. With
 * no scope (no signed-in account yet) nothing is read or cleared.
 */
export function loadNeoConversationId(
  surface: NeoChatSurface,
  scope: NeoConversationScope | null,
  storage: () => ResumeStorage = sessionStore
): string | null {
  if (scope === null) return null;
  const key = neoConversationKey(surface);
  try {
    const store = storage();
    const raw = store.getItem(key);
    if (raw === null) return null;
    const stored = parseStoredNeoConversation(raw);
    if (stored !== null && stored.userId === scope.userId && stored.boatId === scope.boatId) {
      return stored.conversationId;
    }
    store.removeItem(key);
    return null;
  } catch {
    return null;
  }
}

/** Keep the conversation the server answered in, for this surface's next reload. */
export function saveNeoConversationId(
  surface: NeoChatSurface,
  scope: NeoConversationScope | null,
  conversationId: string,
  storage: () => ResumeStorage = sessionStore
): void {
  if (scope === null || !UUID.test(conversationId)) return;
  const record: StoredNeoConversation = {
    userId: scope.userId,
    boatId: scope.boatId,
    conversationId,
  };
  try {
    storage().setItem(neoConversationKey(surface), JSON.stringify(record));
  } catch {
    // Storage blocked or full: a reload starts a new conversation, as before.
  }
}

/** Forget this surface's conversation. */
export function clearNeoConversationId(
  surface: NeoChatSurface,
  storage: () => ResumeStorage = sessionStore
): void {
  try {
    storage().removeItem(neoConversationKey(surface));
  } catch {
    // Nothing to clear if storage never worked.
  }
}

/**
 * True when a chat request that continued `sentConversationId` was refused
 * because the server no longer knows that conversation: deleted, or another
 * account's or boat's. The route answers that in its preamble, before the model
 * is called or anything is stored, so the same message can be sent again in a
 * new conversation. A 404 for any
 * other reason, or one for a request that continued nothing, is not this.
 */
export function isNeoConversationGone(
  sentConversationId: string | null,
  status: number,
  body: unknown
): boolean {
  return (
    sentConversationId !== null &&
    status === 404 &&
    typeof body === 'object' &&
    body !== null &&
    (body as { error?: unknown }).error === 'conversation_not_found'
  );
}
